import React, { useMemo } from 'react';
import { Typography, Box } from '@material-ui/core';
import { ResizableTableV2 } from '../../components/resizableTable/ResizableTableV2';

// Create column definitions for RSM Performance Summary table
// stages: dynamic stage abbreviations from LKP_STAGES_NEW (e.g., ['Q', 'E', 'N', 'P', 'C'])
const createColumns = (fiscalYear, quarters, stages) => {
  // Get quarter label (e.g., "2026-Q1" -> "Q1")
  const getQuarterLabel = (quarter) => {
    const parts = quarter.split('-');
    return parts.length > 1 ? parts[1] : quarter;
  };

  return [
    {
      label: '#',
      sortable: false,
      align: 'center',
      subColumns: [{
        label: '',
        field: 'index',
        type: 'number',
        align: 'center',
        width: 25,
        sortable: false,
      }],
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: 'RSM (Rep)',
      sortable: true,
      align: 'center',
      subColumns: [{
        label: '',
        field: 'repName',
        type: 'text',
        align: 'left',
        width: 100,
        sortable: true,
        filterable: true,
      }],
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: 'Current Qualified\nPipeline ($M)',
      sortable: true,
      align: 'center',
      subColumns: [{
        label: '',
        field: 'pipelineACV',
        type: 'number',
        align: 'right',
        width: 50,
        sortable: true,
      }],
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: 'Current Pipeline Aging (by # of Opps)',
      sortable: false,
      align: 'center',
      subColumns: [
        {
          label: '< 3 mos.',
          field: 'aging_lt3mos',
          type: 'text',
          align: 'center',
          width: 45,
          sortable: true,
        },
        {
          label: '3-6 mos.',
          field: 'aging_3_6mos',
          type: 'text',
          align: 'center',
          width: 45,
          sortable: true,
        },
        {
          label: '6-9 mos.',
          field: 'aging_6_9mos',
          type: 'text',
          align: 'center',
          width: 45,
          sortable: true,
        },
        {
          label: '9-12 mos.',
          field: 'aging_9_12mos',
          type: 'text',
          align: 'center',
          width: 45,
          sortable: true,
        },
        {
          label: '> 12 mos.',
          field: 'aging_gt12mos',
          type: 'text',
          align: 'center',
          width: 45,
          sortable: true,
        },
      ],
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: 'Current Qualified Pipeline % by Stage (by ACV)',
      sortable: false,
      align: 'center',
      // Dynamic stage columns from LKP_STAGES_NEW
      subColumns: stages.map((stageChar) => ({
        label: stageChar,
        field: `stage_${stageChar}`,
        type: 'text',
        align: 'center',
        width: 30,
        sortable: true,
      })),
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: `Annual Quota for\nFY ${fiscalYear} ($M)`,
      sortable: true,
      align: 'center',
      subColumns: [{
        label: '',
        field: 'annualQuota',
        type: 'number',
        align: 'right',
        width: 50,
        sortable: true,
      }],
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: `Won YTD FY${fiscalYear}`,
      sortable: false,
      align: 'center',
      subColumns: [
        {
          label: '# of Opps',
          field: 'wonCount',
          type: 'number',
          align: 'right',
          width: 45,
          sortable: true,
        },
        {
          label: 'ACV ($M)',
          field: 'wonYTD',
          type: 'number',
          align: 'right',
          width: 50,
          sortable: true,
        },
      ],
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
    {
      label: `Quota Attainment for FY ${fiscalYear}`,
      sortable: false,
      align: 'center',
      subColumns: quarters.map((q) => ({
        label: getQuarterLabel(q),
        field: `attainment_${q.replace('-', '_')}`,
        type: 'text',
        align: 'center',
        width: 45,
        sortable: true,
      })),
      headerBgColor: '#4472c4',
      headerTextColor: '#ffffff',
    },
  ];
};

// Transform data for ResizableTable format
// stages: dynamic stage abbreviations from LKP_STAGES_NEW
const transformData = (data, quarters, stages) => {
  return data.map((row) => {
    const transformed = {
      index: row.index,
      repName: row.repName,
      pipelineACV: row.pipelineACV?.toFixed(1) || '0.0',
      annualQuota: row.annualQuota?.toFixed(1) || '0.0',
      wonCount: row.wonCount || 0,
      wonYTD: row.wonYTD?.toFixed(1) || '0.0',
      // Pipeline Aging percentages
      aging_lt3mos: `${row.agingPercent?.['<3mos'] || 0}%`,
      aging_3_6mos: `${row.agingPercent?.['3-6mos'] || 0}%`,
      aging_6_9mos: `${row.agingPercent?.['6-9mos'] || 0}%`,
      aging_9_12mos: `${row.agingPercent?.['9-12mos'] || 0}%`,
      aging_gt12mos: `${row.agingPercent?.['>12mos'] || 0}%`,
    };

    // Add dynamic stage percentages from LKP_STAGES_NEW
    stages.forEach((stageChar) => {
      transformed[`stage_${stageChar}`] = `${row.stagePercent?.[stageChar] || 0}%`;
    });

    // Add quota attainment by quarter
    quarters.forEach((q) => {
      transformed[`attainment_${q.replace('-', '_')}`] = `${row.quotaAttainment?.[q] || 0}%`;
    });

    return transformed;
  });
};

const RSMPerformanceTable = ({ data, fiscalYear, quarters, stages }) => {
  // Hooks must be called before any early returns
  const columns = useMemo(() => createColumns(fiscalYear, quarters || [], stages || []), [fiscalYear, quarters, stages]);
  const rows = useMemo(() => transformData(data || [], quarters || [], stages || []), [data, quarters, stages]);

  // Custom getCellValue to make serial number reflect current sorted position
  const getCellValue = (row, field, rowIndex) => {
    if (field === 'index') {
      return rowIndex + 1; // Return current position (1-based)
    }
    return row[field];
  };

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography variant="body1" color="textSecondary">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <ResizableTableV2
      columns={columns}
      rows={rows}
      rowKey="repName"
      stickyFirstColumn={true}
      maxWidth="100%"
      getCellValue={getCellValue}
    />
  );
};

export default RSMPerformanceTable;
